import React from 'react';
import { Button, Switch, Tag, Tooltip } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { describeCron } from '../Common/Form';
import { IntegrationConfig, RESPONSE_STATE_OPTIONS } from '../Common/Integrations';

/**
 * The row's on/off is `active`, which gates every run — scheduled and by hand
 * alike — so one switch answers for the whole healthcheck. `schedule_enabled`
 * only silences the crontab, and it is edited where the schedule itself is, on
 * the form; a row that offered both would be two switches saying near enough
 * the same word.
 *
 * Read as a negative: the API defaults `active` to true and never returns null,
 * so an absent field means an API that predates it, not a paused healthcheck.
 */
const isActive = (record: any) => record.active !== false;

/** a healthcheck with no crontab is fired by hand only */
const scheduleState = (record: any) => {
    if (!record.schedule_crontab) return 'manual';
    return record.schedule_enabled ? 'on' : 'paused';
};

const HealthcheckActions = ({ record, refresh }: { record: any; refresh: () => void }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const isAdmin = permissions === 'ADMIN';
    const canTrigger = ['ADMIN', 'GENERAL'].includes(permissions);
    const active = isActive(record);

    const trigger = () => {
        dataProvider
            .getAction('serviceowner_healthchecks', { verb: `trigger/service/${record.service?.id}` })
            .then(() => {
                notify('Healthcheck triggered for every in-service item');
                refresh();
            })
            // the API says why it refused — paused, rate limited — and that
            // reads better than a generic failure
            .catch((error: any) => notify(error.body?.trigger || 'Could not trigger the healthcheck', 'error'));
    };

    const setActive = (checked: boolean) => {
        // The API takes a PUT, so every writable field has to travel with the
        // request. Both schedule keys are deliberately left out: the serializer
        // treats a present crontab as "the user changed the schedule" and
        // re-resolves it, which would rewrite the schedule on a pure on/off.
        dataProvider
            .update('serviceowner_healthchecks', {
                id: record.id,
                data: {
                    service: record.service?.id,
                    target_url: record.target_url,
                    authorization: record.authorization,
                    verify: record.verify,
                    timeout: record.timeout,
                    active: checked,
                },
                previousData: { id: record.id },
            })
            .then(() => {
                notify(checked ? 'Healthcheck enabled' : 'Healthcheck paused');
                refresh();
            })
            .catch(() => notify('Could not update the healthcheck', 'error'));
    };

    return (
        <>
            {canTrigger && (
                <Tooltip
                    title={
                        active
                            ? 'Check every in-service item of this service now'
                            : 'The healthcheck is paused — enable it to run a check'
                    }
                >
                    {/* a disabled button fires no mouse events, so the tooltip
                        has to hang off something around it */}
                    <span>
                        <Button
                            type="primary"
                            size="small"
                            icon={<ThunderboltOutlined />}
                            disabled={!active}
                            onClick={trigger}
                        >
                            Trigger
                        </Button>
                    </span>
                </Tooltip>
            )}
            {isAdmin && (
                <Tooltip
                    title={
                        active
                            ? 'Pause this healthcheck — nothing runs, scheduled or by hand'
                            : 'Enable this healthcheck'
                    }
                >
                    <label className={`nio-switch${active ? ' nio-switch--on' : ''}`}>
                        <Switch
                            size="small"
                            checked={active}
                            onChange={setActive}
                            aria-label={`${active ? 'Pause' : 'Enable'} the healthcheck for ${record.service?.name}`}
                        />
                        {active ? 'Enabled' : 'Disabled'}
                    </label>
                </Tooltip>
            )}
        </>
    );
};

const healthcheckConfig: IntegrationConfig = {
    resource: 'serviceowner_healthchecks',
    basePath: '/serviceowner_healthchecks',
    singular: 'Healthcheck',
    plural: 'Healthchecks',
    createLabel: 'New healthcheck',
    emptyTitle: 'No healthchecks yet',
    emptyBody:
        'Add a healthcheck to have NetOrca poll your endpoint on a schedule and record the health of every service item it returns.',

    canWrite: (permissions) => permissions === 'ADMIN',

    // a service owns at most one healthcheck, so the service is its identity
    title: (record) => record.service?.name ?? `Healthcheck #${record.id}`,
    subtitle: () => undefined,

    // A paused healthcheck runs nothing at all, so it is answered first and its
    // schedule stops mattering. Below that, the same words the webhooks use.
    status: (record) => {
        if (!isActive(record)) return { tone: 'muted', label: 'Paused' };
        const state = scheduleState(record);
        if (state === 'on') return { tone: 'good', label: 'Active' };
        if (state === 'paused') return { tone: 'warn', label: 'Schedule paused' };
        return { tone: 'muted', label: 'Manual only' };
    },

    // The pill above already carries the schedule state and the facts line
    // carries the schedule itself, so a badge here says only what neither can.
    badges: (record) => <>{!record.verify && <Tag color="orange">TLS unverified</Tag>}</>,

    meta: (record) => [
        { label: 'Target', value: record.target_url, mono: true, title: record.target_url },
        {
            label: 'Schedule',
            value: record.schedule_crontab ? describeCron(record.schedule_crontab).text : undefined,
            title: record.schedule_crontab,
        },
        { label: 'Timeout', value: record.timeout ? `${record.timeout}s` : undefined },
    ],

    Actions: HealthcheckActions,

    responses: {
        resource: 'serviceowner_healthcheck_responses',
        columns: [
            {
                label: 'Service item',
                filters: ['service_item_id'],
                value: (record) => record.service_item?.name || `Service item #${record.service_item?.id ?? '—'}`,
            },
            {
                // the API filters these by healthcheck, and a service owns at
                // most one, so narrowing by service means picking its healthcheck
                label: 'Service',
                filters: ['healthcheck_id'],
                value: (record) => record.service?.name,
            },
        ],
        filters: [
            {
                name: 'service_item_id',
                label: 'Service item',
                type: 'select',
                resource: 'owner_service_items',
            },
            {
                name: 'healthcheck_id',
                label: 'Service',
                type: 'select',
                resource: 'serviceowner_healthchecks',
                // that endpoint has no /filters/ route, and a healthcheck has no
                // name of its own — it is known by the service it probes
                optionsFrom: 'list',
                mapOption: (record: any) => ({
                    value: record.id,
                    label: record.service?.name ?? `Healthcheck #${record.id}`,
                    text: record.service?.name ?? `Healthcheck #${record.id}`,
                }),
            },
            {
                name: 'state',
                label: 'State',
                type: 'select',
                options: RESPONSE_STATE_OPTIONS,
            },
        ],
        emptyText:
            'Responses appear here once a healthcheck runs — one per service item, whether it was scheduled or triggered by hand.',
    },
};

export default healthcheckConfig;
